import React from 'react';
import { useTranslation } from 'react-i18next';
import { Dialog, DialogBackdrop, DialogPanel } from '@headlessui/react';
import {
  XMarkIcon,
  CheckIcon,
  InformationCircleIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';

/* ============================================================
   <Modal> — shared shell
   ------------------------------------------------------------
   Owns the scrim, the cream paper panel, the rounded-pill close
   button, the focus trap, and the open/close transitions. Every
   redesigned modal (BookingConfirmation, Dialog, Space, Filters)
   renders inside it.
   ============================================================ */

const SIZE_WIDTHS = {
  sm: 'max-w-[520px]',
  md: 'max-w-[760px]',
  lg: 'max-w-[1080px]',
  xl: 'max-w-[1260px]',
};

export function Modal({
  isOpen,
  onClose,
  size = 'md',
  children,
  className = '',
  initialFocus,
  // Below lg every modal renders as a bottom sheet (like BookingBubble's
  // mobile panel); pass false to keep the centered card on all sizes.
  sheetOnMobile = true,
}) {
  return (
    <Dialog
      open={!!isOpen}
      as="div"
      className="relative z-[1000]"
      onClose={onClose}
      initialFocus={initialFocus}
    >
      {/* Scrim */}
      <DialogBackdrop
        transition
        className="fixed inset-0 bg-ink/40 backdrop-blur-[2px] transition
                   ease-out duration-200 data-[leave]:ease-in data-[leave]:duration-150
                   data-[closed]:opacity-0"
      />

      {/* Panel */}
      <div className="fixed inset-0 overflow-y-auto">
        <div
          className={
            sheetOnMobile
              ? 'flex min-h-full items-end justify-center lg:items-center lg:p-7'
              : 'flex min-h-full items-center justify-center p-4 sm:p-7'
          }
        >
          <DialogPanel
            transition
            className={[
              'w-full',
              SIZE_WIDTHS[size] || SIZE_WIDTHS.md,
              'relative bg-paper shadow-modal',
              'flex flex-col overflow-hidden max-h-[92vh]',
              'transition data-[leave]:ease-in',
              sheetOnMobile
                ? 'rounded-t-[22px] rounded-b-none lg:rounded-[22px] ' +
                  'ease-out duration-300 data-[leave]:duration-200 ' +
                  'data-[closed]:translate-y-full lg:data-[closed]:translate-y-2 ' +
                  'lg:data-[closed]:opacity-0 lg:data-[closed]:scale-95'
                : 'rounded-[22px] ease-out duration-200 data-[leave]:duration-150 ' +
                  'data-[closed]:opacity-0 data-[closed]:translate-y-2 sm:data-[closed]:scale-95',
              className,
            ].join(' ')}
          >
            <ModalCloseButton onClose={onClose} />
            {children}
          </DialogPanel>
        </div>
      </div>
    </Dialog>
  );
}

function ModalCloseButton({ onClose }) {
  const { t } = useTranslation();
  return (
    <button
      type="button"
      onClick={onClose}
      aria-label={t('common.close', 'Close')}
      className="absolute top-4 right-4 z-20 grid place-items-center w-9 h-9
                 rounded-full bg-white/70 border border-line text-ink
                 hover:bg-white transition"
    >
      <XMarkIcon className="w-4 h-4" />
    </button>
  );
}

/* ============================================================
   Editorial header / footer primitives
   ============================================================ */

export function ModalEyebrow({ children, tone = 'muted', className = '' }) {
  const tones = {
    muted: 'text-muted',
    brand: 'text-yellow-04',
    danger: 'text-bad',
    good: 'text-good',
  };
  return (
    <div
      className={`font-display text-[11px] font-bold uppercase tracking-[0.14em] ${
        tones[tone] || tones.muted
      } ${className}`}
    >
      {children}
    </div>
  );
}

export function ModalTitle({ children, size = 'md', className = '' }) {
  const sizes = {
    sm: 'text-2xl',
    md: 'text-[30px]',
    lg: 'text-[44px]',
  };
  return (
    <h2
      className={`font-serif font-normal leading-[1.05] tracking-[-0.01em] text-ink ${
        sizes[size] || sizes.md
      } ${className}`}
    >
      {children}
    </h2>
  );
}

export function ModalHead({
  eyebrow,
  title,
  sub,
  chip,
  size = 'md',
  className = '',
}) {
  return (
    <div
      className={`px-6 sm:px-9 pt-9 pb-7 flex items-start justify-between gap-6
                  border-b border-line bg-gradient-to-br from-brand-soft/30 to-paper ${className}`}
    >
      <div className="min-w-0">
        {eyebrow && <ModalEyebrow tone="brand">{eyebrow}</ModalEyebrow>}
        <div className={eyebrow ? 'mt-3' : ''}>
          <ModalTitle size={size}>{title}</ModalTitle>
        </div>
        {sub && (
          <p className="mt-3 max-w-[56ch] text-[14.5px] leading-[1.45] text-muted-2">
            {sub}
          </p>
        )}
      </div>
      {chip && <div className="shrink-0">{chip}</div>}
    </div>
  );
}

export function ModalFoot({ children, className = '' }) {
  return (
    <div
      className={`mt-auto flex items-center gap-3 flex-wrap
                  border-t border-line bg-paper-2 px-6 sm:px-9 py-5 ${className}`}
    >
      {children}
    </div>
  );
}

/* ============================================================
   <Chip> — brand-soft pill with optional leading icon
   ============================================================ */

export function Chip({ children, tone = 'brand', icon = null, className = '' }) {
  const tones = {
    brand: 'bg-brand-soft border-[rgba(255,189,89,0.4)] text-yellow-04',
    neutral: 'bg-paper-2 border-line text-ink-2',
  };
  return (
    <span
      className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full
                  text-[12.5px] font-semibold border ${
                    tones[tone] || tones.brand
                  } ${className}`}
    >
      {icon === 'info' && <InformationCircleIcon className="w-3.5 h-3.5" />}
      {children}
    </span>
  );
}

/* ============================================================
   <Pip> — square rounded status glyph (success / info / danger)
   ============================================================ */

export function Pip({ variant = 'info', className = '' }) {
  const map = {
    success: { bg: 'bg-good-soft text-good', Icon: CheckIcon },
    info: { bg: 'bg-paper-2 text-ink', Icon: InformationCircleIcon },
    danger: { bg: 'bg-bad-soft text-bad', Icon: ExclamationTriangleIcon },
  };
  const v = map[variant] || map.info;
  const { Icon } = v;
  return (
    <div
      className={`shrink-0 w-11 h-11 rounded-xl grid place-items-center ${v.bg} ${className}`}
    >
      <Icon className="w-5 h-5" />
    </div>
  );
}

/* ============================================================
   <Switch> — custom toggle (bg-ink when on)
   ============================================================ */

export function Switch({ checked, onChange, ariaLabel }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={!!checked}
      aria-label={ariaLabel}
      onClick={() => onChange?.(!checked)}
      className={`relative shrink-0 w-10 h-[22px] rounded-full transition-colors
                  focus:outline-none focus-visible:ring-2 focus-visible:ring-ink/30 ${
                    checked ? 'bg-ink' : 'bg-line-strong'
                  }`}
    >
      <span
        className={`absolute top-[3px] left-[3px] w-4 h-4 rounded-full bg-white shadow
                    transition-transform ${checked ? 'translate-x-[18px]' : ''}`}
      />
    </button>
  );
}

export default Modal;
